<template>
    <div class="data-page">
        <PageHeader title="Jogadores" :subtitle="summaryText">
            <template #actions>
                <Button class="p-button-outlined" type="button" icon="pi pi-arrow-left" label="Voltar para clientes"
                    @click="goToClients" />
            </template>
        </PageHeader>

        <div class="data-card">
            <div class="data-filters">
                <div class="data-filters-main">
                    <span class="p-input-icon-left data-search">
                        <i class="pi pi-search" />
                        <InputText type="text" v-model="state.filter.name" placeholder="Buscar por nome..."
                            @keyup.enter="applyFilters" />
                    </span>

                    <div class="data-filters-actions">
                        <Button class="p-button-outlined" type="button"
                            :icon="state.showFilters ? 'pi pi-chevron-up' : 'pi pi-sliders-h'"
                            :label="activeFilters.length ? `Filtros (${activeFilters.length})` : 'Filtros'"
                            @click="state.showFilters = !state.showFilters" />
                        <Button type="button" icon="pi pi-search" label="Pesquisar" :loading="state.loading.players"
                            @click="applyFilters" />
                    </div>
                </div>

                <div v-show="state.showFilters" class="data-filters-grid">
                    <div class="filter-field">
                        <label for="filter-player-id">ID</label>
                        <InputText id="filter-player-id" v-model="state.filter.id" @keyup.enter="applyFilters" />
                    </div>

                    <div class="filter-field">
                        <label for="filter-player-cpf">CPF/CNPJ</label>
                        <InputText id="filter-player-cpf" v-model="state.filter.cpf" placeholder="Somente números"
                            @keyup.enter="applyFilters" />
                    </div>

                    <div class="filter-field">
                        <label for="filter-player-email">Login/Email</label>
                        <InputText id="filter-player-email" v-model="state.filter.email" @keyup.enter="applyFilters" />
                    </div>

                    <div class="filter-field">
                        <label for="filter-player-code">Code</label>
                        <InputText id="filter-player-code" v-model="state.filter.code" @keyup.enter="applyFilters" />
                    </div>

                    <div class="filter-field">
                        <label for="filter-player-status">Status</label>
                        <Dropdown inputId="filter-player-status" showClear v-model="state.filter.status"
                            optionLabel="name" optionValue="value" :options="statusOptions" placeholder="Todas" />
                    </div>
                </div>

                <div v-if="activeFilters.length" class="data-filters-chips">
                    <span v-for="chip in activeFilters" :key="chip.key" class="filter-chip">
                        <span class="filter-chip-label">{{ chip.label }}:</span> {{ chip.value }}
                        <button type="button" class="filter-chip-clear" :aria-label="`Remover filtro ${chip.label}`"
                            @click="clearFilter(chip.key)">
                            <i class="pi pi-times"></i>
                        </button>
                    </span>
                    <Button class="p-button-text p-button-sm" type="button" icon="pi pi-filter-slash"
                        label="Limpar filtros" @click="clearAllFilters" />
                </div>
            </div>

            <DataTable class="data-table" lazy paginator :value="state.players" :loading="state.loading.players"
                dataKey="id" ref="dt" :autoLayout="true" responsiveLayout="scroll" :first="state.first"
                :rows="state.perPage" :totalRecords="state.totalRecords" :rowsPerPageOptions="[20, 50, 100]"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                @page="onPage($event)">

                <template #empty>
                    <div class="data-empty">
                        <i class="pi pi-inbox"></i>
                        <span>{{ activeFilters.length ? 'Nenhum jogador para os filtros aplicados.' : 'Nenhum jogador registrado.' }}</span>
                    </div>
                </template>

                <Column field="id" header="ID" class="col-id">
                    <template #body="slotProps">
                        <span class="record-id">{{ slotProps.data.id }}</span>
                    </template>
                </Column>

                <Column field="name" header="Nome">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.name" class="record-name">{{ slotProps.data.name }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>

                <Column field="cpf" header="CPF/CNPJ">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.cpf" class="mono-chip mono-chip-sm">{{ formatDocument(slotProps.data.cpf) }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>

                <Column field="email" header="Login/Email">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.email" class="mono-chip" :title="slotProps.data.email">{{ slotProps.data.email }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>

                <Column field="code" header="Code">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.code" class="mono-chip mono-chip-sm">{{ slotProps.data.code }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>

                <Column field="status" header="Status">
                    <template #body="slotProps">
                        <span class="pill pill-status" :class="slotProps.data.status == 'BLOCKED' ? 'is-off' : 'is-on'">
                            <span class="pill-dot"></span>
                            {{ changeStatusBoleanToText(slotProps.data.status) }}
                        </span>
                    </template>
                </Column>

                <Column class="col-actions">
                    <template #header>
                        Ações
                    </template>
                    <template #body="slotProps">
                        <div class="flex justify-content-end">
                            <SplitButton class="table-action-button" label="Transações" icon="pi pi-eye"
                                :model="actionMenuItems(slotProps.data)"
                                @click="openTransactionsModal(slotProps.data)" />
                        </div>
                    </template>
                </Column>

                <template #paginatorstart>
                    <TableResults :shown="state.players.length" :total="state.totalRecords"
                        :pageTotal="state.players.length" />
                </template>
            </DataTable>
        </div>

        <Dialog class="data-dialog" v-model:visible="state.modalIsOpen" :style="{ width: '32rem' }" modal
            :header="isBlocked ? 'Liberar jogador' : 'Bloquear jogador'" @update:visible="handleClose">

            <div class="data-form-grid data-form-single">

                <div class="data-dialog-subject">
                    <span class="pill pill-muted">ID {{ state.player.id }}</span>
                    <span>{{ state.player.name || formatDocument(state.player.cpf) }}</span>
                </div>

                <Message :severity="isBlocked ? 'info' : 'warn'" sticky :closable="false">
                    <span v-if="isBlocked">
                        Depois da liberação, o jogador portador do CPF/CNPJ {{ formatDocument(state.player.cpf) }}
                        volta a poder realizar qualquer tipo de transação.
                    </span>
                    <span v-else>
                        Depois do bloqueio, o jogador portador do CPF/CNPJ {{ formatDocument(state.player.cpf) }}
                        não consegue realizar nenhum tipo de transação.
                    </span>
                </Message>

            </div>

            <template #footer>
                <Button class="p-button-text" type="button" label="Cancelar" @click="handleClose" />
                <Button :class="isBlocked ? '' : 'p-button-danger'"
                    :loading="state.loading.tryingToChangePlayerStatus" type="button"
                    :icon="isBlocked ? 'pi pi-undo' : 'pi pi-ban'"
                    :label="isBlocked ? 'Liberar jogador' : 'Bloquear jogador'" @click="checkPlayerStatus" />
            </template>

        </Dialog>

        <Dialog class="data-dialog data-dialog-embed" v-model:visible="state.transactionsModalIsOpen" modal
            :style="{ width: '92vw' }" :header="transactionsModalHeader" @update:visible="handleTransactionsModalClose">

            <Transactions v-if="state.transactionsModalIsOpen" :player="state.player" />

        </Dialog>

    </div>
</template>

<script>

import InputText from 'primevue/inputtext'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Message from 'primevue/message'
import Dropdown from 'primevue/dropdown'
import SplitButton from 'primevue/splitbutton'
import PageHeader from '../../components/PageHeader.vue'
import TableResults from '../../components/TableResults.vue'
import Transactions from '../../pages/Transactions/Transactions.vue'
import { computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import services from '../../services'
import useNotificationToast from '../../composables/useNotificationToast'

export default {

    components: { InputText, DataTable, Column, Button, Dialog, Message, Dropdown, SplitButton, PageHeader, TableResults, Transactions },

    setup() {

        const state = reactive({

            filter: {
                id: '',
                cpf: '',
                name: '',
                email: '',
                code: '',
                status: null,
            },

            showFilters: false,

            currentPage: 1,
            perPage: 20,
            totalRecords: 0,
            first: 0,

            players: [],

            player: {},

            loading: {
                players: false,
                tryingToChangePlayerStatus: false
            },

            modalIsOpen: false,
            transactionsModalIsOpen: false
        })

        // o Dropdown guarda o valor que a API espera
        const statusOptions = [
            { name: 'Ativo', value: 'ACTIVE' },
            { name: 'Bloqueado', value: 'BLOCKED' },
        ]

        const store = useStore()
        const route = useRoute()
        const router = useRouter()
        const { success, error } = useNotificationToast()

        loadPlayers()

        function loadPlayers() {

            state.loading.players = true

            // cópia do filtro: a query string é montada no serviço e não pode alterar o estado da tela
            const params = {
                filter: {
                    id: state.filter.id,
                    cpf: state.filter.cpf,
                    name: state.filter.name,
                    email: state.filter.email,
                    code: state.filter.code,
                    status: state.filter.status ?? '',
                },
                page: state.currentPage,
                perPage: state.perPage,
            }

            const token = store.getters.getToken

            services.players.getAll({ clientId: route.params.id, params, token })
                .then((response) => {
                    state.players = response.data.data
                    state.currentPage = response.data.meta.current_page
                    state.totalRecords = response.data.meta.total
                })
                .catch(() => {
                    error("Não foi possível carregar os jogadores.")
                })
                .finally(() => { state.loading.players = false })
        }

        // toda mudança de filtro volta para a primeira página
        function applyFilters() {
            state.currentPage = 1
            state.first = 0
            loadPlayers()
        }

        function onPage(event) {
            state.currentPage = event.page + 1
            state.perPage = event.rows
            state.first = event.first
            loadPlayers()
        }

        const activeFilters = computed(() => {
            const chips = []

            if (state.filter.id) chips.push({ key: 'id', label: 'ID', value: state.filter.id })
            if (state.filter.name) chips.push({ key: 'name', label: 'Nome', value: state.filter.name })
            if (state.filter.cpf) chips.push({ key: 'cpf', label: 'CPF/CNPJ', value: formatDocument(state.filter.cpf) })
            if (state.filter.email) chips.push({ key: 'email', label: 'Login/Email', value: state.filter.email })
            if (state.filter.code) chips.push({ key: 'code', label: 'Code', value: state.filter.code })
            if (state.filter.status) chips.push({ key: 'status', label: 'Status', value: changeStatusBoleanToText(state.filter.status) })

            return chips
        })

        const summaryText = computed(() => {
            const total = state.totalRecords
            const parts = [`Cliente #${route.params.id}`, `${total} ${total === 1 ? 'jogador encontrado' : 'jogadores encontrados'}`]

            if (activeFilters.value.length) {
                parts.push(`${activeFilters.value.length} ${activeFilters.value.length === 1 ? 'filtro aplicado' : 'filtros aplicados'}`)
            }

            return parts.join(' · ')
        })

        function clearFilter(key) {
            state.filter[key] = key === 'status' ? null : ''
            applyFilters()
        }

        function clearAllFilters() {
            state.filter.id = ''
            state.filter.cpf = ''
            state.filter.name = ''
            state.filter.email = ''
            state.filter.code = ''
            state.filter.status = null

            applyFilters()
        }

        function changeStatusBoleanToText(status) {
            return status == 'BLOCKED' ? 'Bloqueado' : 'Ativo'
        }

        // a coluna aceita CPF e CNPJ; qualquer outro formato é exibido como veio
        function formatDocument(document) {

            const digits = String(document || '').replace(/\D/g, '')

            if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')

            if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')

            return document || '—'

        }

        const isBlocked = computed(() => state.player.status == 'BLOCKED')

        const transactionsModalHeader = computed(() => {
            const player = state.player.name || formatDocument(state.player.cpf)
            return player ? `Transações de ${player}` : 'Transações'
        })

        function actionMenuItems(player) {

            if (player.status == 'BLOCKED') {
                return [{
                    label: 'Liberar jogador',
                    icon: 'pi pi-undo',
                    command: () => openConfirmationModal(player)
                }]
            }

            return [{
                label: 'Bloquear jogador',
                icon: 'pi pi-ban',
                class: 'text-red-400',
                command: () => openConfirmationModal(player)
            }]

        }

        function openTransactionsModal(player) {
            state.player = player
            state.transactionsModalIsOpen = true
        }

        function openConfirmationModal(player) {
            state.player = player
            state.modalIsOpen = true
        }

        function handleClose() {
            state.modalIsOpen = false
        }

        function handleTransactionsModalClose() {
            state.transactionsModalIsOpen = false
        }

        function checkPlayerStatus() {

            state.loading.tryingToChangePlayerStatus = true

            // o status muda depois do recarregamento; guarda a intenção da ação atual
            const blocked = isBlocked.value

            const token = store.getters.getToken
            const id = route.params.id
            const cpf = state.player.cpf

            const request = blocked
                ? services.clients.unblockPlayer({ id, cpf, token })
                : services.clients.blockPlayer({ id, cpf, token })

            request
                .then(() => {
                    success(blocked ? 'O jogador foi liberado!' : 'O jogador foi bloqueado!')
                    state.modalIsOpen = false
                    loadPlayers()
                })
                .catch(() => {
                    error(blocked ? 'Não foi possível liberar o jogador.' : 'Não foi possível bloquear o jogador.')
                })
                .finally(() => {
                    state.loading.tryingToChangePlayerStatus = false
                })

        }

        function goToClients() {
            router.push({ name: 'Clients' })
        }

        return {
            state,
            statusOptions,
            activeFilters,
            summaryText,
            isBlocked,
            transactionsModalHeader,
            applyFilters,
            clearFilter,
            clearAllFilters,
            changeStatusBoleanToText,
            formatDocument,
            actionMenuItems,
            openTransactionsModal,
            openConfirmationModal,
            handleClose,
            handleTransactionsModalClose,
            checkPlayerStatus,
            goToClients,
            onPage,
            loadPlayers
        }

    }

}

</script>
